import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import React from 'react'

import { CAMPAIGN_SIDEBAR_WIDTH } from '@/app/module/campaigns/definitions'

type CampaignSidebarOption = {
  active?: boolean
  highlight?: boolean
  icon?: string
  label: string
  name: string
  right?: React.ReactNode
  onClick: () => void
}

type Props = {
  campaignName: string
  footer?: React.ReactNode
  options: CampaignSidebarOption[]
  onBack?: () => void
}

const CampaignSidebar: React.FC<Props> = ({ campaignName, footer, options, onBack }) => {
  const activeOption = options.find((option) => option.active) || options[0]
  const selectedValue = activeOption?.name || ''

  const handleMobileOptionChange = React.useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedOption = options.find((option) => option.name === event.target.value)
      selectedOption?.onClick()
    },
    [options],
  )

  return (
    <>
      <Box
        data-testid="campaign-sidebar-mobile"
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          display: { xs: 'flex', md: 'none' },
          flexShrink: 0,
          gap: 1.5,
          minHeight: 64,
          px: { xs: 2, sm: 2.5 },
          py: 1,
          width: '100%',
        }}
      >
        {!!onBack && (
          <IconButton
            aria-label="Back to campaigns"
            data-testid="campaign-sidebar-mobile-back"
            edge="start"
            size="small"
            sx={{
              flexShrink: 0,
              ml: -0.5,
            }}
            onClick={onBack}
          >
            <Icon>chevron_left</Icon>
          </IconButton>
        )}
        <Select
          aria-label="Campaign section"
          data-testid="campaign-sidebar-mobile-select"
          disabled={!options.length}
          disableUnderline
          displayEmpty
          SelectDisplayProps={{
            'aria-label': 'Campaign section',
          }}
          size="small"
          value={selectedValue}
          variant="filled"
          renderValue={() => <MobileOptionContent option={activeOption} />}
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 1,
            flexShrink: 0,
            ml: 'auto',
            maxWidth: { xs: 180, sm: 220 },
            minWidth: { xs: 132, sm: 156 },
            '&:hover': {
              bgcolor: 'action.selected',
            },
            '&.Mui-focused': {
              bgcolor: 'action.hover',
            },
            '& .MuiSelect-select': {
              alignItems: 'center',
              display: 'flex',
              fontSize: 14,
              fontWeight: 700,
              gap: 0.75,
              minHeight: 36,
              px: 1.25,
              py: 0.75,
            },
            '& .MuiSelect-icon': {
              right: 6,
            },
          }}
          onChange={handleMobileOptionChange}
        >
          {options.map((option) => (
            <MenuItem
              data-highlighted={option.highlight ? 'true' : 'false'}
              data-testid={`campaign-sidebar-mobile-option-${option.name}`}
              key={option.name}
              value={option.name}
              sx={
                option.highlight
                  ? {
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                      '& .MuiIcon-root': {
                        color: 'warning.contrastText',
                      },
                      '&:hover': {
                        bgcolor: 'warning.dark',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'warning.main',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: 'warning.dark',
                      },
                    }
                  : undefined
              }
            >
              <MobileOptionContent option={option} showRight />
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Drawer
        anchor="left"
        sx={{
          display: { xs: 'none', md: 'block' },
          flexShrink: 0,
          justifyContent: 'center',
          width: CAMPAIGN_SIDEBAR_WIDTH,

          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            paddingTop: '64px',
            width: CAMPAIGN_SIDEBAR_WIDTH,
          },
        }}
        variant="permanent"
      >
        <List id="campaign-editor-sidebar">
          <ListItem disablePadding divider>
            <ListItemButton disabled disableRipple>
              <Typography className="campaignTitle" sx={{}} variant="subtitle1">
                {campaignName}
              </Typography>
            </ListItemButton>
          </ListItem>
          {options.map((option, i) => (
            <ListItem
              key={i}
              disablePadding
              divider
              secondaryAction={option.right}
              sx={
                option.highlight
                  ? {
                      '& .MuiListItemSecondaryAction-root .MuiIcon-root': {
                        color: 'white',
                      },
                    }
                  : undefined
              }
            >
              <ListItemButton
                className={`campaign-sidebar-option option-${i}`}
                data-highlighted={option.highlight ? 'true' : 'false'}
                data-testid={`campaign-sidebar-option-${option.name}`}
                selected={option.active}
                sx={
                  option.highlight
                    ? {
                        backgroundColor: 'warning.light',
                        color: 'warning.contrastText',
                        '& .MuiListItemIcon-root': {
                          color: 'warning.contrastText',
                        },
                        '&:hover': {
                          backgroundColor: 'warning.dark',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'warning.main',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'warning.dark',
                        },
                      }
                    : undefined
                }
                onClick={option.onClick}
              >
                {!!option.icon && (
                  <ListItemIcon>
                    <Icon>{option.icon}</Icon>
                  </ListItemIcon>
                )}
                <ListItemText primary={option.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {footer}
      </Drawer>
    </>
  )
}

type MobileOptionContentProps = {
  option?: CampaignSidebarOption
  showRight?: boolean
}

const MobileOptionContent: React.FC<MobileOptionContentProps> = ({ option, showRight }) => (
  <Box
    data-testid={showRight ? undefined : 'campaign-sidebar-mobile-current-option'}
    sx={{
      alignItems: 'center',
      display: 'flex',
      gap: 0.75,
      minWidth: 0,
      width: '100%',
    }}
  >
    {!!option?.icon && <Icon sx={{ color: 'text.secondary', fontSize: 18 }}>{option.icon}</Icon>}
    <Typography
      component="span"
      noWrap
      sx={{
        flex: 1,
        fontSize: 14,
        fontWeight: 700,
        minWidth: 0,
      }}
    >
      {option?.label || ''}
    </Typography>
    {showRight && option?.right && (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexShrink: 0,
          ml: 1,
        }}
      >
        {option.right}
      </Box>
    )}
  </Box>
)

export default CampaignSidebar
